import {ApiErrorCode} from '@constants/errorCodes';
import type {TFunction, TOptions} from 'i18next';
import type {DataSelector} from '@deezer/next/shared/utils/csv';
import {encodeCSVDataUrl, json2csv} from '@deezer/next/shared/utils/csv';
import type {PaginatedGet} from '@deezer/next/services/api/Starmania/requests/types';

export const isErrorCode = (
	error: string | number,
	errorReference: ApiErrorCode | ApiErrorCode[],
) => {
	if (Array.isArray(errorReference)) {
		return errorReference.includes(Number(error));
	}
	return Number(error) === errorReference;
};

export const getErrorContent = (
	errorCode: number | string | undefined,
	t: TFunction,
	options?: TOptions,
): {
	title: string | undefined;
	description: string;
	status: 'warning' | 'error' | 'info';
} => {
	let title: string | undefined = t(
		'errormessage_title_somethingwentwrong_d4c-web',
	);
	let description = t('toastmessage_text_unknownerroroccurred_d4c-web');
	let status: 'warning' | 'error' | 'info' = 'error';
	switch (Number(errorCode)) {
		case ApiErrorCode.TOKEN_EXPIRED:
		case ApiErrorCode.TOKEN_INVALID:
		case ApiErrorCode.TOKEN_MALFORMED:
		case ApiErrorCode.UNKNOWN_REVOKED_TOKEN:
		case ApiErrorCode.DISABLED_ACCOUNT:
			title = undefined;
			description = t('errormessage_text_sessionexpired_d4c-web');
			status = 'warning';
			break;
		case ApiErrorCode.EMAIL_ALREADY_USED_CREATEPASSWORD:
		case ApiErrorCode.EMAIL_ALREADY_USED_SIGNUP:
			title = t('errormessage_title_emailalreadyused_d4c-web');
			description = t(
				'errormessage_text_accountusingemailalreadyexists_d4c-web',
			);
			break;
		case ApiErrorCode.PASSWORD_NOT_STRONG_ENOUGH:
			title = t('toastmessage_title_passwordnotstrongenough_d4c-web');
			description = t('toastmessage_text_passwordnotstrongenough_d4c-web');
			break;
		case ApiErrorCode.MANAGER_ALREADY_CLAIMED_THIS_ARTIST:
			title = t('errormessage_title_artistalreadyclaimed_d4c-web');
			description = t('errormessage_text_artistalreadyclaimed_d4c-web');
			break;
		case ApiErrorCode.INVALID_UPC_PROVIDER:
			title = t('errormessage_title_upcerror_d4c-web');
			description = t('errormessage_text_upcerror_d4c-web');
			break;
		case ApiErrorCode.ALREADY_CLAIMED_PROVIDER:
			title = t('errormessage_title_providererror_d4c-web');
			description = t('errormessage_text_providererror_d4c-web');
			break;
		case ApiErrorCode.NOT_MATCHING_UPC_PROVIDER:
			title = t('errormessage_title_upcerror_d4c-web');
			description = t('errormessage_text_upcnotmatching_d4c-web');
			break;
		case ApiErrorCode.PROVIDER_NEEDED:
			title = t('errormessage_text_providermissing_d4c-web');
			description = t('errormessage_text_providerneeded_d4c-web');
			break;
		case ApiErrorCode.ARTIST_ALREADY_CLAIMED_BY_THIS_USER:
			title = t('errormessage_title_youhavealreadyrequestedaccess_d4c-web');
			description = t(
				'errormessage_text_artistalreadyclaimedbysomeonelse_d4c-web',
			);
			break;
		case ApiErrorCode.MISSING_PARAMETER_SIGNUP:
		case ApiErrorCode.MISSING_PARAMETER_CREATEACCOUNT:
		case ApiErrorCode.PITCH_BODY_PARAMETER_MISSING:
		case ApiErrorCode.MISSING_PARAMETER_CLAIM_ARTIST:
		case ApiErrorCode.MISSING_PARAMETER_CLAIM_PROVIDER:
		case ApiErrorCode.PITCHES_IMPORT_MISSING_PARAMETER:
			title = t('errormessage_title_aparameterismissing_d4c-web');
			description = t('errormessage_text_aparameterismissing_d4c-web');
			break;
		case ApiErrorCode.TOO_MANY_REQUESTS:
			title = t('errormessage_title_toomanyrequests_d4c-web');
			description = t('errormessage_text_toomanyrequests_d4c-web');
			break;
		case ApiErrorCode.LABEL_ALREADY_HAS_ACCESS:
			title = t('errormessage_title_labelalreadyhasaccess_d4c-web');
			description = t('errormessage_text_labelalreadyhasaccess_d4c-web');
			break;
		case ApiErrorCode.PITCH_FORBIDDEN_ACCESS:
		case ApiErrorCode.PITCH_DELETE_FORBIDDEN_ACCESS:
		case ApiErrorCode.PITCH_EDIT_FORBIDDEN_ACCESS:
		case ApiErrorCode.SUB_LABEL_ACCESS_DENIED:
		case ApiErrorCode.PITCHES_IMPORT_FORBIDDEN_ACCESS:
		case ApiErrorCode.PITCHES_FILTERS_FORBIDDEN_ACCESS:
		case ApiErrorCode.PITCHES_FILTERS_ACCESS_FORBIDDEN:
			title = t('errormessage_title_forbiddenaccess_d4c-web');
			description = t('errormessage_text_noaccesstothisresource_d4c-web');
			break;
		case ApiErrorCode.PITCH_UPC_FORMAT_INVALID:
		case ApiErrorCode.INVALID_UPC_CLAIM_LABEL:
		case ApiErrorCode.INVALID_UPC_CLAIM_ARTIST_MANAGER:
		case ApiErrorCode.CLAIM_REQUEST_INVALID_UPC:
			title = t('errormessage_title_upcerror_d4c-web');
			description = t('toastmessagepitchingtool_text_upcinvalid_d4c-web');
			break;
		case ApiErrorCode.PITCH_PRIORITY_INVALID:
			title = t('errormessagepitchingtool_title_priorityerror_d4c-web');
			description = t(
				'errormessagepitchingtool_text_priorityprovidednotvalid_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_EMPTY_BODY:
		case ApiErrorCode.CLAIM_REQUEST_EMPTY_MANAGER:
		case ApiErrorCode.CLAIM_REQUEST_EMPTY_LABEL:
			title = t('toastmessage_title_emptyform_d4c-web');
			description = t('toastmessage_text_emptyform_d4c-web');
			break;
		case ApiErrorCode.PITCH_INVALID_STAKEHOLDER_TYPE:
		case ApiErrorCode.PITCHES_IMPORT_INVALID_STAKEHOLDER_TYPE:
		case ApiErrorCode.PITCHES_FILTERS_INVALID_STAKEHOLDER_TYPE:
			title = t('toastmessage_title_invalidstakeholdertype_d4c-web');
			description = t('toastmessage_text_invalidstakeholdertype_d4c-web');
			break;
		case ApiErrorCode.PITCH_LABEL_NOT_PART_OF_YOUR_ACCESS:
			title = t(
				'toastmessagepitchingtool_title_labelnotpartofyouraccess_d4c-web',
			);
			description = t(
				'toastmessagepitchingtool_text_labelnotpartofyouraccess_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_IS_NOT_YOURS_DELETE:
		case ApiErrorCode.PITCH_IS_NOT_YOURS_EDIT:
		case ApiErrorCode.PITCH_DOES_NOT_BELONG_TO_YOU:
			title = t('toastmessagepitchingtool_title_pitchnotyours_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_canteditordeletepitchnotyours_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_LINK_INVALID_EDIT:
		case ApiErrorCode.PITCH_LINK_INVALID_CREATE:
			title = t('errormessagepitchingtool_title_invalidlisteninglink_d4c-web');
			description = t('toastmessagepitchingtool_text_linkinvalid_d4c-web');
			break;
		case ApiErrorCode.PITCH_24H_BEFORE_RELEASE_DELETE:
		case ApiErrorCode.PITCH_24H_BEFORE_RELEASE_EDIT:
			title = t('toastmessagepitchingtool_title_releasetooclose_d4c-web');
			description = t('toastmessagepitchingtool_text_releasetooclose_d4c-web');
			break;
		case ApiErrorCode.PITCH_CANNOT_SET_PASSED_DATE_FOR_RELEASE:
			title = t('toastmessagepitchingtool_title_releasedateerror_d4c-web');
			description = t('toastmessagepitchingtool_text_releasedateerror_d4c-web');
			break;
		case ApiErrorCode.PITCH_CANNOT_SET_NEW_RELEASE_DATE_TO_24H:
			title = t('toastmessagepitchingtool_title_releasedateerror_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_releasedatewithin24h_d4c-web',
			);
			break;
		case ApiErrorCode.CLAIM_DELETE_NOT_FOUND:
			title = t('toastmessage_title_claimnotfound_d4c-web');
			description = t('toastmessage_text_claimnotfound_d4c-web');
			break;
		case ApiErrorCode.CLAIM_DELETE_NOT_YOURS:
			title = t('toastmessage_title_claimnotyours_d4c-web');
			description = t('toastmessage_text_claimnotyours_d4c-web');
			break;
		case ApiErrorCode.CLAIM_DELETE_NOT_IN_PROGRESS:
			title = t('toastmessage_title_claimnotinprogress_d4c-web');
			description = t('toastmessage_text_claimnotinprogress_d4c-web');
			break;
		case ApiErrorCode.CLAIM_REQUEST_INVALID_PARAMETER_MANAGER:
		case ApiErrorCode.CLAIM_REQUEST_INVALID_PARAMETER_LABEL:
			title = t('toastmessagepitchingtool_title_fieldsinvalid_d4c-web');
			description = t('toastmessagepitchingtool_text_fieldsinvalid_d4c-web');
			break;
		case ApiErrorCode.CANNOT_CLAIM_USER_ALREADY_HAS_ACCESS:
			title = t('toastmessage_title_useralreadyhasaccess_d4c-web');
			description = t('toastmessage_text_useralreadyhasaccess_d4c-web');
			break;
		case ApiErrorCode.NO_CLAIM_REQUESTS:
			title = t('toastmessage_title_noclaimrequests_d4c-web');
			description = t('toastmessage_text_noclaimrequests_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_FILE_MISSING:
			title = t('errormessagepitchingtool_title_filemissing_d4c-web');
			description = t(
				'errormessagepitchingtool_text_providefiletoimport_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_UPLOADER_ERROR:
			title = t('errormessagepitchingtool_title_fileerror_d4c-web');
			description = t(
				'errormessagepitchingtool_text_errorwhileuploadingfile_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_UPC:
			title = t('errormessage_title_upcerror_d4c-web');
			description = t('errormessage_text_upcerror_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_PRIORITY:
		case ApiErrorCode.PITCHES_FILTERS_PRIORITY_DOES_NOT_EXISTS:
			title = t('errormessagepitchingtool_title_priorityerror_d4c-web');
			description = t(
				'errormessagepitchingtool_text_priorityprovidednotvalid_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_RELEASE_ALREADY_PITCHED:
			title = t('errormessagepitchingtool_title_releasealreadypitched_d4c-web');
			description = t(
				'errormessagepitchingtool_text_releasealreadyreleased_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_UNAUTHORIZED_LABEL:
			title = t('errormessagepitchingtool_title_unauthorizedlabel_d4c-web');
			description = t(
				'errormessagepitchingtool_text_unauthorizedlabelinfield_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_FILE_TOO_BIG:
			title = t('errormessagepitchingtool_title_filetoobig_d4c-web');
			description = t(
				'errormessagepitchingtool_text_filetouploadtoobig_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_LABEL_NOT_FOUND:
			title = t('errormessagepitchingtool_title_labelnotfound_d4c-web');
			description = t(
				'errormessagepitchingtool_text_labelinfieldnotfound_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_PROVIDER_NOT_FOUND:
			title = t('errormessagepitchingtool_title_providernotfound_d4c-web');
			description = t(
				'errormessagepitchingtool_text_providerinfieldnotfound_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_WRONG_ARTIST_GENDER:
			title = t('toastmessagepitchingtool_title_artistgendererror_d4c-web');
			description = t('toastmessagepitchingtool_text_genderisinvalid_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_LISTENING_LINK:
			title = t('errormessagepitchingtool_title_invalidlisteninglink_d4c-web');
			description = t(
				'errormessagepitchingtool_text_linkinfieldinvalid_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_FILTERS_DATE_INVALID:
		case ApiErrorCode.PITCH_IMPORT_INVALID_DATE_FORMAT:
			title = t('toastmessagepitchingtool_title_dateerror_d4c-web');
			description = t('toastmessagepitchingtool_text_dateisinvalid_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_RELEASE_DATE_PAST:
			title = t('toastmessagepitchingtool_title_releasedateerror_d4c-web');
			description = t('toastmessagepitchingtool_text_releasedateerror_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_RELEASE_DATE_WRONG_FORMAT:
			title = t('toastmessagepitchingtool_title_releasedateerror_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_releasedatewrongformat_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_TYPE:
			title = t('toastmessagepitchingtool_title_invalidtype_d4c-web');
			description = t('toastmessagepitchingtool_text_invalidtype_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_CONFIG:
			title = t('toastmessagepitchingtool_title_invalidconfiguration_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_invalidconfiguration_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_GENRE:
		case ApiErrorCode.PITCH_GENRE_INVALID:
		case ApiErrorCode.PITCHES_FILTERS_GENRE_DOES_NOT_EXISTS:
			title = t('toastmessagepitchingtool_title_invalidgenre_d4c-web');
			description = t('toastmessagepitchingtool_text_invalidgenre_d4c-web');
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_EDITO_TEAM:
		case ApiErrorCode.PITCHES_FILTERS_EDITO_TEAM_DOES_NOT_EXISTS:
			title = t('toastmessagepitchingtool_title_invalideditorialteam_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_invalideditorialteam_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_ISRC_INVALID:
		case ApiErrorCode.PITCH_EDIT_ISRC_INVALID:
			title = t('toastmessagepitchingtool_title_invalidisrc_d4c-web');
			description = t('toastmessagepitchingtool_text_isrcisinvalid_d4c-web');
			break;
		case ApiErrorCode.PITCHES_FILTERS_USER_IS_NOT_AM_USER:
			title = t('toastmessagepitchingtool_title_accessnotallowed_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_notallowedtoaccessresource_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_FILTERS_STATUS_DOES_NOT_EXISTS:
			title = t('toastmessagepitchingtool_title_statusdoesntexist_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_providedstatusdoesntexist_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_FILTERS_SORT_INVALID:
			title = t('toastmessagepitchingtool_title_sortingerror_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_sortingoptionunavailable_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_IMPORT_INVALID_TEMPLATE_FORMAT:
			title = t('toastmessagepitchingtool_title_invalidcsvformat_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_csvfiledoesnotmatchtemplate_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_DOES_NOT_EXISTS:
			title = t('toastmessagepitchingtool_title_pitchdoesnotexist_d4c-web');
			description = t(
				'toastmessagepitchingtool_text_thispitchdoesnotexist_d4c-web',
			);
			break;
		case ApiErrorCode.PITCH_DELETED:
			title = t('toastmessagepitchingtool_title_pitchdeleted_d4c-web');
			description = t('errormessage_text_cantaccessdeletedpitch_d4c-web');
			break;
		case ApiErrorCode.PITCHES_CREATE_WITH_SAME_UPC_ALREADY_TARGET_THIS_MARKET:
		case ApiErrorCode.PITCHES_EDIT_WITH_SAME_UPC_ALREADY_TARGET_THIS_MARKET:
			title = t('errormessage_title_editoteamerror_d4c-web');
			description = t(
				'errormessage_text_releasealreadypitchedtoeditoteam_d4c-web',
			);
			break;
		case ApiErrorCode.PITCHES_TOO_MUCH_FILTERS:
			// fixme translate
			title = 'Filter creation error';
			description =
				'You have reached the maximum capacity for custom filters. Consider removing others';
			break;
		case ApiErrorCode.CANNOT_DELETE_ACCOUNT_ADMIN_WITH_USERS:
			// fixme translate
			title = 'Account deletion error';
			description =
				'An admin can only delete his account if he has no attached users. Either pass the admin role to another user or unlink users attached to your account';
			break;
		case ApiErrorCode.OKTA_BASIC_LOGIN_NOT_AVAILABLE:
			description = t(
				'oktaautherrormessage_text_basicloginnotavailableuseokta_d4c-web',
			);
			break;
		case ApiErrorCode.OKTA_ACCOUNT_CANNOT_BE_DELETED_OR_UPDATED:
			description = `${
				t('oktaautherrormessage_text_accountcantbeupdated_d4c-web') +
				t('oktaautherrormessage_text_accountcantbeupdated_d4c-web')
			}`;
			break;
		case ApiErrorCode.OKTA_USER_CANNOT_RESET_PASSWORD:
			description = t(
				'oktaautherrormessage_text_cantresetthisaccountpassword_d4c-web',
			);
			break;
		case ApiErrorCode.OKTA_USER_CANNOT_UPDATE_PASSWORD:
			description = t(
				'oktaautherrormessage_text_cantresetthisaccountpassword_d4c-web',
			);
			break;
		case ApiErrorCode.CAMPAIGN_BUDGET_TOO_LOW:
		case ApiErrorCode.CAMPAIGN_CREATE_BUDGET_TOO_LOW:
		case ApiErrorCode.CAMPAIGN_EDIT_BUDGET_TOO_LOW:
			description = t('campaignmgrerror_text_budgetbelowXminamount_d4c-web', {
				options,
			});
			break;
		case ApiErrorCode.CAMPAIGN_48H_DELAY:
			// fixme translate
			title = 'Date error';
			description = 'You cannot create a campaign within the 48 next hours';
			break;
		case ApiErrorCode.CAMPAIGN_ARTIST_TOO_SMALL:
			// fixme translate
			description =
				'The artist selected does not match our requirement for campaign creation';
			break;
		case ApiErrorCode.CAMPAIGN_ALREADY_SET_FOR_THIS_UPC:
			// fixme translate
			title = 'Date error';
			description =
				'A campaign is already set for this release in the given period';
			break;

		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_EMAIL:
			// fixme translate
			title = 'Invalid email';
			description = 'The email used is not a valid email';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_FIRSTNAME:
			// fixme translate
			title = 'Invalid firstname';
			description =
				'Firstname is not valid, it needs to be between 1 and 100 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_LASTNAME:
			// fixme translate
			title = 'Invalid lastname';
			description =
				'Lastname is not valid, it needs to be between 1 and 100 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_COMPANY_NAME:
			// fixme translate
			title = 'Invalid company name';
			description =
				'Company name is not valid, it needs to be between 0 and 100 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_ADDRESS:
			// fixme translate
			title = 'Invalid address';
			description =
				'Address is not valid, it needs to be between 0 and 250 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_PHONE_NUMBER:
			// fixme translate
			title = 'Invalid phone number';
			description = 'The phone number used is not a valid phone number';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_CLAIM_TYPE:
			// fixme translate
			title = 'Invalid claim type';
			description = 'Claim type is not part of a valid choice';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_CLAIM_CONTENT_TYPE:
			// fixme translate
			title = 'Invalid claim content type';
			description = 'Claim content type is not part of a valid choice';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_CLAIM_NATURE:
			// fixme translate
			title = 'Invalid claim nature';
			description = 'Claim nature is not part of a valid choice';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_ORIGINAL_CONTENT_LINKS:
			// fixme translate
			title = 'Invalid original content links';
			description =
				'Original content links is not valid, it needs to be between 0 and 500 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_REASON:
			// fixme translate
			title = 'Invalid reason';
			description =
				'Reason is not valid, it needs to be between 1 and 1000 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_RIGHTSHOLDER_RELATIONSHIP:
			// fixme translate
			title = 'Invalid rightsholder relationship';
			description = 'Rightsholder relationship is not part of a valid choice';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_RIGHTSHOLDER_NAME:
			// fixme translate
			title = 'Invalid rightsholder name';
			description =
				'Rightsholder name is not valid, it needs to be between 0 and 100 characters';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_RIGHTSHOLDER_EMAIL:
			// fixme translate
			title = 'Invalid rightsholder email';
			description = 'Rightsholder email is not a valid email';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_AGENT_TO_RIGHTSHOLDER_RELATIONSHIP:
			// fixme translate
			title = 'Invalid agent to rightsholder relationship';
			description =
				'Agent to rightsholder relationship is not part of a valid choice';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_VALID_COUNTRIES:
			// fixme translate
			title = 'Invalid countries';
			description = 'Countries are not valid';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIMS_WRONG_PRIVILEGES:
		case ApiErrorCode.COPYRIGHT_CLAIMS_FORBIDDEN_ACCESS_DASHBOARD:
			// fixme translate
			title = t('errormessage_title_forbiddenaccess_d4c-web');
			description = 'You do not have access to the copyright claim dashboard';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NOT_FOUND:
			// fixme translate
			title = 'Not found';
			description = 'This claim does not exists or has not been found';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_PENDING_CLAIM_SOLVING:
			// fixme translate
			title = 'Unable to solve case';
			description = 'Only a case pending can be solved';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_CANNOT_DENY_CLOSED_CLAIM:
			// fixme translate
			title = 'Unable to deny a claim';
			description = 'Cannot deny a claim already closed';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NO_CASE_ON_THIS_CLAIM:
			// fixme translate
			title = 'Case not found';
			description = 'No case found on this claim';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_MISSING_FEEDBACK_SOLVE:
			// fixme translate
			title = 'Missing feedback parameter';
			description =
				'Please ensure to provide a feedback that matches this case';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_MISSING_COMMENT_SOLVE:
			// fixme translate
			title = 'Missing comment parameter';
			description = 'Please ensure to provide a comment for your feedback';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_CANNOT_SOLVE_CLOSED_CLAIM:
			// fixme translate
			title = 'Unable to solve case';
			description = 'Cannot solve a closed claim';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_SOLVE_INVALID_STATUS:
			// fixme translate
			title = 'Invalid status';
			description = 'Only pending or to close case can be solved';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_NO_CONTENT_ON_THIS_CLAIM:
			// fixme translate
			title = 'Claim not found';
			description = 'Content not found on this claim';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_MISSING_EMAIL_ON_EDITION:
			// fixme translate
			title = 'Missing email parameter';
			description = 'Please ensure to provide an email';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_INVALID_EMAIL_ON_EDITION:
			// fixme translate
			title = 'Invalid email provided';
			description = 'Please ensure to provide a valid email';
			break;
		case ApiErrorCode.COPYRIGHT_CLAIM_CANNOT_ACKNOWLEDGE_CLAIM_AT_LEAST_ONE_EMAIL_MISSING:
			// fixme translate
			title = 'Unable to acknowledge this claim';
			description =
				'Please ensure you provided email for all cases of this claim';
			break;
		default:
			break;
	}
	return {title, description, status};
};

export const downloadCSVExport = async <T, U>(
	filename: string,
	paginatedDataFetcher: PaginatedGet<T>,
	extractData: (data: T) => U[],
	csvFields: DataSelector<U>[],
	options: Partial<{
		pageSize: number;
		chunkSize: number;
		maxPages: number;
	}> = {},
) => {
	const {pageSize = 100, chunkSize = 5, maxPages} = options;
	const firstPage = await paginatedDataFetcher(1, pageSize);
	const {
		meta: {
			pagination: {numberOfPages},
		},
	} = firstPage;
	if (!extractData(firstPage.data).length && numberOfPages < 1) {
		return;
	}
	const totalPages =
		typeof maxPages !== 'undefined'
			? Math.min(maxPages, numberOfPages)
			: numberOfPages;
	const promiseChunks = [...Array(totalPages - 1)]
		.map(async (_, i) => {
			const nextPage = await paginatedDataFetcher(i + 2, pageSize);
			return nextPage.data;
		})
		.reduce((acc, p, i) => {
			const chunkIndex = Math.floor(i / chunkSize);
			if (!acc[chunkIndex]) {
				acc[chunkIndex] = [p];
			} else {
				acc[chunkIndex].push(p);
			}
			return acc;
		}, [] as any);
	let allResults = [firstPage.data];

	for (let i = 0; i < promiseChunks.length; i++) {
		const results = await Promise.all(promiseChunks[i]);
		allResults = [...allResults, ...results];
	}

	const data = allResults.map(extractData).flat();
	const csv = json2csv(data, csvFields);

	const link = document.createElement('a');
	if (link.download !== undefined) {
		link.setAttribute('href', encodeCSVDataUrl(csv));
		link.setAttribute('download', filename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
};
